import { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, errorMessageFromBody, isNetworkError, NETWORK_ERROR_MESSAGE } from '../../services/api';

export interface ObservanceItem {
  id?: string;
  name: string;
  date: string;
  description: string;
  type: string;
}

export interface ObservanceListResponse {
  status: string;
  message?: string | null;
  observances?: ObservanceItem[];
  start_date?: string;
  end_date?: string;
  /** The user's pick limit this cycle (by plan). */
  max_selections?: number;
}

interface ObservancePickerProps {
  title: string;
  /** Label for the dates badge, e.g. "Subscription" or "This cycle". */
  cycleLabel?: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  country: string;
  onCountryChange: (country: string) => void;
  /** Selection keys, "IN:Maha Saptami". */
  selected: string[];
  onToggle: (key: string) => void;
  /** "Select Top N": the keys to add. */
  onSelectMany: (keys: string[]) => void;
  max: number;
  onMaxChange?: (max: number) => void;
  error?: string;
  onClearError?: () => void;
  /** Loads the observance list (setup: the subscription tenure; Profile: this cycle). */
  loadObservances: (country: string) => Promise<ObservanceListResponse>;
  /** View only (a cycle already planned): the list and the selections are
   * shown, nothing can be picked, removed or added; the country can still be switched to view. */
  readOnly?: boolean;
}

const COUNTRY_LABELS: Record<string, string> = {
  IN: 'India', US: 'United States', GB: 'United Kingdom', AU: 'Australia',
  CA: 'Canada', NG: 'Nigeria', ZA: 'South Africa', SG: 'Singapore',
  AE: 'UAE', PH: 'Philippines',
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const LOAD_ERROR = "Observances couldn't be loaded. Please try again.";

function formatCycleDate(dateStr: string) {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * The observance chooser shared by Social Media Manager setup (Step 3) and
 * Profile → Social Media: on/off switch, country, month tabs with the
 * observances (every region's festivals, found by AI web search) and custom
 * events, up to `max` picks, "Select Top N" and Add Custom Event.
 */
export default function ObservancePicker({
  title,
  cycleLabel = 'Cycle',
  enabled,
  onEnabledChange,
  country,
  onCountryChange,
  selected,
  onToggle,
  onSelectMany,
  max,
  onMaxChange,
  error,
  onClearError,
  loadObservances,
  readOnly = false,
}: ObservancePickerProps) {
  const [errorFlash, setErrorFlash] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [observances, setObservances] = useState<ObservanceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [cycleStart, setCycleStart] = useState('');
  const [cycleEnd, setCycleEnd] = useState('');
  const [showCustomEventModal, setShowCustomEventModal] = useState(false);
  const [customEventName, setCustomEventName] = useState('');
  const [customEventDate, setCustomEventDate] = useState('');
  const [customEventDesc, setCustomEventDesc] = useState('');
  const [customEventError, setCustomEventError] = useState('');
  const [addingCustomEvent, setAddingCustomEvent] = useState(false);

  // Local YYYY-MM-DD for "today": observances before it are locked.
  const todayStr = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }, []);

  const applyList = (data: ObservanceListResponse) => {
    if (data.status === 'success') {
      setObservances(data.observances || []);
      setCycleStart(data.start_date || '');
      setCycleEnd(data.end_date || '');
      if (typeof data.max_selections === 'number') onMaxChange?.(data.max_selections);
      setLoadError('');
    } else {
      setLoadError(data.message || LOAD_ERROR);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadObservances(country)
      .then((data) => { if (!cancelled) applyList(data); })
      .catch((err) => {
        console.error('Error fetching observances:', err);
        if (!cancelled) setLoadError(LOAD_ERROR);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [country]);

  const handleObservanceClick = (obs: ObservanceItem) => {
    if (!enabled) return;
    if (obs.date < todayStr) return; // past observances are locked
    const key = `${country}:${obs.name}`;
    if (!selected.includes(key) && selected.length >= max) {
      setErrorFlash(true);
      setTimeout(() => setErrorFlash(false), 300);
    }
    onToggle(key);
  };

  // Fill the remaining picks with the earliest upcoming observances in the list.
  const handleSelectTop = () => {
    if (!enabled) return;
    const keys = [...observances]
      .filter((o) => o.date >= todayStr)
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((o) => `${country}:${o.name}`)
      .filter((k) => !selected.includes(k))
      .slice(0, Math.max(max - selected.length, 0));
    onSelectMany(keys);
  };

  const closeCustomEventModal = () => {
    setShowCustomEventModal(false);
    setCustomEventError('');
  };

  const handleAddCustomEvent = async () => {
    if (!customEventName || !customEventDate || addingCustomEvent) return;
    const userId = localStorage.getItem('user_id') || '';
    const token = localStorage.getItem('access_token');
    setAddingCustomEvent(true);
    setCustomEventError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/smm/onboarding/custom-event?user_id=${userId}&event_name=${encodeURIComponent(customEventName)}&event_date=${customEventDate}&description=${encodeURIComponent(customEventDesc)}`,
        { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errorMessageFromBody(body, res.status));
      // The endpoint answers 200 with {status: 'error', message} when saving fails.
      if (body?.status === 'error') throw new Error(body.message || "The event couldn't be saved. Please try again.");
    } catch (err: any) {
      console.error('Error adding custom event:', err);
      setCustomEventError(isNetworkError(err) ? NETWORK_ERROR_MESSAGE : err?.message || "The event couldn't be saved. Please try again.");
      setAddingCustomEvent(false);
      return;
    }
    // Saved: close the modal, then refresh the list (a refresh failure shows in the list).
    setAddingCustomEvent(false);
    closeCustomEventModal();
    setCustomEventName('');
    setCustomEventDate('');
    setCustomEventDesc('');
    try {
      applyList(await loadObservances(country));
    } catch (err) {
      console.error('Error fetching observances:', err);
      setLoadError(LOAD_ERROR);
    }
  };

  const observancesByMonth = useMemo(() => {
    const grouped: Record<string, ObservanceItem[]> = {};
    observances.forEach((obs) => {
      const month = obs.date.substring(0, 7); // YYYY-MM
      (grouped[month] ||= []).push(obs);
    });
    return grouped;
  }, [observances]);
  const availableMonths = Object.keys(observancesByMonth).sort();
  const activeMonthKey = availableMonths[selectedMonth] || '';
  const activeObservances = observancesByMonth[activeMonthKey] || [];
  const selectTopDisabled = selected.length >= max || loading || !!loadError || observances.length === 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-teal-50 rounded-lg flex items-center justify-center">
            <i className="ri-calendar-event-line text-teal-600 text-sm"></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
          {enabled && cycleStart && cycleEnd && (
            <span className="text-[10px] bg-gray-50 text-gray-500 border border-gray-200 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
              <i className="ri-calendar-2-line mr-1"></i>
              {cycleLabel}: {formatCycleDate(cycleStart)} – {formatCycleDate(cycleEnd)}
            </span>
          )}
        </div>
        <label className={`relative inline-flex items-center ${readOnly ? 'cursor-default opacity-60' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            checked={enabled}
            disabled={readOnly}
            onChange={() => { onEnabledChange(!enabled); onClearError?.(); }}
            className="sr-only peer"
            aria-label={`${title} on or off`}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-300 rounded-full peer peer-checked:bg-teal-500 transition-colors"></div>
          <div className="absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform peer-checked:translate-x-5"></div>
        </label>
      </div>

      {enabled && (
        <div>
          {/* Country */}
          <div className="mb-4 flex gap-3">
            <select
              value={country}
              onChange={(e) => onCountryChange(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-teal-500"
            >
              <option value="IN">India</option>
              <option value="US">United States</option>
              <option value="GB">United Kingdom</option>
              <option value="CA">Canada</option>
              <option value="AU">Australia</option>
            </select>

            {!readOnly && (
              <button
                onClick={() => setShowCustomEventModal(true)}
                className="ml-auto px-4 py-2 bg-teal-500 text-white text-xs font-medium rounded-lg hover:bg-teal-600 transition-colors"
              >
                <i className="ri-add-line mr-1"></i>
                Add Custom Event
              </button>
            )}
          </div>
          {readOnly ? (
            <p className="text-xs text-gray-500 mb-3">
              <i className="ri-lock-line mr-1"></i>
              Already planned — the selected observances are shown; they can't be changed here.
            </p>
          ) : (
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-gray-500">Select up to {max} observance posts to integrate</span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleSelectTop}
                disabled={selectTopDisabled}
                className={`text-[10px] px-3 py-1.5 rounded-full font-semibold transition-all whitespace-nowrap cursor-pointer ${selectTopDisabled
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100'
                  }`}
              >
                <i className="ri-checkbox-multiple-line mr-1"></i>
                Select Top {max}
              </button>
            </div>
          </div>
          )}

          {/* Error */}
          {error && (
            <div
              className={`mb-4 flex items-center gap-2 text-xs bg-amber-50 text-amber-700 px-3 py-2 rounded-lg border border-amber-200 transition-all ${errorFlash ? 'scale-[1.02]' : 'scale-100'}`}
            >
              <i className="ri-error-warning-line"></i>
              {error}
            </div>
          )}

          {/* Month Selector Tabs */}
          {loading ? (
            <div className="text-center py-4 text-xs text-gray-400">Loading observances...</div>
          ) : loadError ? (
            <div className="flex items-center gap-2 text-xs bg-amber-50 text-amber-700 px-3 py-3 rounded-lg border border-amber-200">
              <i className="ri-error-warning-line"></i>
              {loadError}
            </div>
          ) : (
            <>
              <div className="flex gap-1 overflow-x-auto pb-2 mb-3 scrollbar-thin">
                {availableMonths.map((monthKey, idx) => {
                  const monthName = MONTH_NAMES[parseInt(monthKey.split('-')[1]) - 1];
                  const obsCount = observancesByMonth[monthKey].length;
                  const isSelected = idx === selectedMonth;
                  return (
                    <button
                      key={monthKey}
                      onClick={() => setSelectedMonth(idx)}
                      className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all cursor-pointer border whitespace-nowrap ${isSelected
                        ? 'bg-teal-50 border-teal-200 text-teal-700 shadow-sm'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-teal-200 hover:text-teal-600'
                        }`}
                    >
                      <span className="uppercase tracking-wider">{monthName.substring(0, 3)}</span>
                      {obsCount > 0 && (
                        <span className="ml-1 inline-flex items-center justify-center w-3.5 h-3.5 bg-teal-100 text-teal-600 rounded-full text-[8px] font-bold">
                          {obsCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Observance List */}
              <div className="rounded-xl border border-gray-200 p-4 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-800">
                    {activeMonthKey ? MONTH_NAMES[parseInt(activeMonthKey.split('-')[1]) - 1] : ''} Observances
                  </h4>
                  {activeObservances.length > 0 && (
                    <span className="text-[10px] bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full font-medium">
                      {activeObservances.length} event{activeObservances.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {activeObservances.length > 0 ? (
                  <div className="space-y-2">
                    {activeObservances.map((obs, idx) => {
                      const key = `${country}:${obs.name}`;
                      const isSelected = selected.includes(key);
                      const isPast = obs.date < todayStr;
                      const maxReached = selected.length >= max && !isSelected;
                      return (
                        <button
                          key={idx}
                          onClick={() => handleObservanceClick(obs)}
                          disabled={readOnly || isPast || maxReached}
                          title={readOnly ? undefined : isPast ? 'This date has already passed' : undefined}
                          className={`w-full text-left p-3 rounded-lg border transition-all ${isSelected
                            ? `bg-teal-500 text-white border-teal-500 shadow-sm ${readOnly ? 'cursor-default' : 'cursor-pointer'}`
                            : readOnly
                              ? 'bg-white text-gray-600 border-gray-200 cursor-default'
                              : isPast
                              ? 'bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed opacity-60'
                              : maxReached
                                ? 'bg-gray-50 text-gray-300 border-gray-200 cursor-not-allowed'
                                : 'bg-white text-gray-700 border-teal-200 hover:bg-teal-50 cursor-pointer'
                            }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                {isSelected && <i className="ri-check-line text-xs"></i>}
                                <span className="text-xs font-semibold">{obs.name}</span>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full ${isSelected ? 'bg-teal-600 text-white' : 'bg-teal-100 text-teal-700'}`}>
                                  {obs.type}
                                </span>
                                {isPast && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">Past</span>
                                )}
                              </div>
                              <p className={`text-[10px] ${isSelected ? 'text-teal-100' : 'text-gray-500'}`}>{obs.description}</p>
                            </div>
                            <span className={`text-xs font-medium ${isSelected ? 'text-teal-100' : 'text-gray-400'}`}>
                              {new Date(obs.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic text-center py-4">No observances for this month</p>
                )}
              </div>
            </>
          )}

          {/* Selection Summary */}
          {selected.length > 0 && (
            <div className="mt-4 rounded-xl border border-teal-200 bg-teal-50/50 p-4">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-bookmark-line text-teal-600 text-sm"></i>
                <span className="text-xs font-semibold text-teal-800">
                  Selected Observances ({selected.length}/{max})
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {selected.map((selection) => {
                  const [obsCountry, obsName] = selection.includes(':') ? selection.split(':', 2) : [country, selection];
                  return (
                    <span
                      key={selection}
                      className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-white border border-teal-300 shadow-sm text-xs font-medium text-teal-900"
                    >
                      <i className="ri-calendar-check-line text-teal-500 text-[11px]"></i>
                      <span>{obsName}</span>
                      <span className="text-[10px] text-teal-500 font-normal">&bull; {COUNTRY_LABELS[obsCountry] || obsCountry}</span>
                      {!readOnly && (
                        <button
                          onClick={() => onToggle(selection)}
                          className="w-4 h-4 ml-0.5 flex items-center justify-center rounded-full bg-teal-100 hover:bg-teal-200 text-teal-600 hover:text-teal-800 transition-colors cursor-pointer"
                          title={`Remove ${obsName}`}
                        >
                          <i className="ri-close-line text-[10px]"></i>
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {!enabled && (
        <p className="text-xs text-gray-400 italic">Toggle on to enable observance-aware content scheduling</p>
      )}

      {/* Custom Event Modal */}
      {showCustomEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Add Custom Event</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Event Name</label>
                <input
                  type="text"
                  value={customEventName}
                  onChange={(e) => setCustomEventName(e.target.value)}
                  placeholder="Product Launch, Webinar, etc."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Date</label>
                <input
                  type="date"
                  value={customEventDate}
                  onChange={(e) => setCustomEventDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 block mb-1">Description (Optional)</label>
                <textarea
                  value={customEventDesc}
                  onChange={(e) => setCustomEventDesc(e.target.value)}
                  placeholder="Brief description of the event"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
                />
              </div>
            </div>
            {customEventError && (
              <div className="mt-3 flex items-start gap-2 text-xs bg-red-50 text-red-700 px-3 py-2 rounded-lg border border-red-200">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{customEventError}</span>
              </div>
            )}
            <div className="flex gap-2 mt-4">
              <button
                onClick={closeCustomEventModal}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCustomEvent}
                disabled={!customEventName || !customEventDate || addingCustomEvent}
                className="flex-1 px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addingCustomEvent ? 'Adding…' : 'Add Event'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
